import type { IPresentedParameter } from '../../../domain/configurations';
import type { SearchResult } from '../../../domain/search-results';

/**
 * The AnswerItemRendererEvent is dispatched when the user has changed the answer a row holds and is done changing it.
 *
 * One event for every kind of parameter a row can answer, because there is one thing to do about any of them: the new value goes onto the parameter and the position is priced again. Which kind it was is on the parameter itself — {@link IPresentedParameter} is a union of classes, a model's and a stock item's, so
 * `instanceof` narrows it, the same way the row decides what to show. A kind added later needs no event of its own.
 *
 * Two types, because a row holds two things the user can change: the answer itself, and — on a lookup that has one — the side it applies to. Both end the same way, with a configuration made again around the changed parameter, so they are one class; which of the two moved is the type, and not a field a listener has to
 * remember to read.
 *
 * The row's own children say nothing outside it. A lookup's picker, a text field and the side dropdown each raise their own events, in their own shapes, at their own moments; the row stops all of them and says this instead, so a listener never has to know what a row is made of.
 */
export default class AnswerItemRendererEvent extends Event {
	/**
	 * The static ANSWER_CHANGED type is dispatched when the user has changed the answer a row holds.
	 *
	 * Only for a change the user made, and only once it is settled: typing is not a change until the field is left, and leaving a field nobody touched is not one at all.
	 *
	 * Example: addEventListener(AnswerItemRendererEvent.ANSWER_CHANGED, (event: AnswerItemRendererEvent) => {});
	 */
	public static ANSWER_CHANGED = 'AnswerItemRendererEvent.answerChanged';
	/**
	 * The static SIDE_CHANGED type is dispatched when the user has picked another side for a lookup that applies to one.
	 *
	 * Only for a side the user picked, and only when it is not the one the parameter already holds: the row picks that one itself each time it is handed the parameter, and says nothing about it.
	 *
	 * Example: addEventListener(AnswerItemRendererEvent.SIDE_CHANGED, (event: AnswerItemRendererEvent) => {});
	 */
	public static SIDE_CHANGED = 'AnswerItemRendererEvent.sideChanged';
	/** The parameter whose answer the user changed. Still holding the old value: writing the new one is for whoever catches this. */
	public get parameter(): IPresentedParameter {
		return this.#parameter;
	}

	/**
	 * The item the user picked from a lookup's popover, or null when there was none to pick.
	 *
	 * Null for a text parameter, which has no collection to pick from, and null for a lookup whose key was typed straight in by someone who already knew it. So a listener that needs the thing itself rather than what it is called has to be able to find it from {@link AnswerItemRendererEvent.value}.
	 */
	public get selectedItem(): SearchResult | null {
		return this.#selectedItem;
	}

	/**
	 * The answer as the user left it.
	 *
	 * The key for a lookup — picked or typed, it is the key either way — and the literal value for a text parameter. For SIDE_CHANGED it is the side picked, as the engine's code: `T`, `VI`.
	 */
	public get value(): string {
		return this.#value;
	}

	#parameter: IPresentedParameter;

	#selectedItem: SearchResult | null;

	#value: string;

	public constructor(type: string, parameter: IPresentedParameter, value: string, selectedItem: SearchResult | null = null) {
		super(type, { bubbles: true });
		this.#parameter = parameter;
		this.#selectedItem = selectedItem;
		this.#value = value;
	}
}
